// MACHINERY V2 (Stroje) — profile page body, layout C (M2.4).
//
// Split out of MachineProfileV2.tsx for the 300-line cap. MachineProfileV2 owns
// the ROUTE concerns (fetch, id guard, telemetry, derivations, tab resolution)
// and hands a loaded machine down; this file is the render for that machine —
// and therefore the only branch that mounts actions, dialogs and toasts. A
// component that only exists once a machine is loaded cannot dereference an
// undefined one, which is why the split falls here and not elsewhere.
//
// STRUCTURE (artboard 0504, manager ruling 2026-08-22 — designer layout wins):
//     breadcrumb -> [decommission banner] -> HERO -> pill tabs -> tab body
// No left identity column (folded into the hero), no KPI strip (§9 D3 cut it).
//
// The readiness card sits at the TOP OF THE TAB BODY AREA: the layout-C hi-fi
// is silent on readiness placement — see the header of MachineProfileV2.tsx for
// the full reasoning and the gate.
//
// No Tailwind, no shadcn, no MUI, no @emotion, no sx, no dt tokens.
import React from "react";

import useTranslation from "../../../../shared/hooks/useTranslation";
import { getMachineDisplayName } from "../../../../shared/utils/machineUtils";
import { Machine } from "../../models/models";
import MachineryToastHost from "../feedback/MachineryToastHost";
import {
  MachineryV2CardPresentation,
  MachineryV2Telemetry,
} from "../fleet/cardStates";
import ReadinessPopupV2 from "../readiness/ReadinessPopupV2";
import ReadinessProfileCardV2 from "../readiness/ReadinessProfileCardV2";
import { MachineryV2ReadinessPresentation } from "../readiness/readinessPresentation";
import ProfileDecommissionBanner from "./ProfileDecommissionBanner";
import ProfileDialogs from "./ProfileDialogs";
import ProfileHero from "./ProfileHero";
import ProfileTabBody from "./ProfileTabBodies";
import ProfileTabs from "./ProfileTabs";
import { MachineryV2ProfileTab } from "./profileTabContract";
import { useProfileActions } from "./useProfileActions";

export interface ProfileBodyProps {
  machine: Machine;
  telemetry: MachineryV2Telemetry;
  presentation: MachineryV2CardPresentation;
  tabs: MachineryV2ProfileTab[];
  activeTab: MachineryV2ProfileTab;
  onTabChange: (t: MachineryV2ProfileTab) => void;
  readiness: MachineryV2ReadinessPresentation | null;
  readinessOpen: boolean;
  onReadinessOpenChange: (open: boolean) => void;
  onNavigateFleet: () => void;
}

const ProfileBody: React.FC<ProfileBodyProps> = ({
  machine,
  telemetry,
  presentation,
  tabs,
  activeTab,
  onTabChange,
  readiness,
  readinessOpen,
  onReadinessOpenChange,
  onNavigateFleet,
}) => {
  const { translate } = useTranslation("myMachinery");
  const {
    actions,
    onCreateServiceReport,
    onCreateServiceRequest,
    onRestore,
    onDelete,
    deleteBusy,
    dialogProps,
  } = useProfileActions(machine);

  // ONE derivation, fed to BOTH the readiness card and its popup (M2.4
  // review). They disagreed: the card fell back through the CATALOG hierarchy
  // (`catalogModel.manufacturer.name`) while the popup read only the top-level
  // `machine.manufacturer.name`, so a sub-brand machine (Tajmac-ZPS ›
  // Manurhin) named one manufacturer on the card and a different one in the
  // popup's "not offered by X" line. Single derivation is exactly what
  // readinessPresentation exists to guarantee, one level up.
  const manufacturerName =
    machine.catalogModel?.manufacturer?.name || machine.manufacturer?.name;

  return (
    <div
      style={{ display: "flex", flexDirection: "column", flex: 1, minHeight: 0, minWidth: 0 }}
    >
      {/* Breadcrumb — artboard 0504: "Strojový park › <machine>". */}
      <div
        style={{
          padding: "10px 24px",
          borderBottom: "1px solid var(--border-soft)",
          display: "flex",
          alignItems: "center",
          gap: 8,
          fontSize: 12.5,
          color: "var(--muted)",
          minWidth: 0,
        }}
      >
        <button
          type="button"
          onClick={onNavigateFleet}
          style={{
            padding: 0,
            border: 0,
            background: "transparent",
            cursor: "pointer",
            font: "inherit",
            color: "var(--mod-accent)",
          }}
        >
          {translate("myMachinery:breadcrumbEquipment")}
        </button>
        <span style={{ color: "var(--faint)" }}>›</span>
        <span
          style={{
            color: "var(--ink-2)",
            fontWeight: 500,
            minWidth: 0,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {getMachineDisplayName(machine)}
        </span>
      </div>

      <main
        style={{
          flex: 1,
          minHeight: 0,
          minWidth: 0,
          overflowY: "auto",
          padding: "16px 24px 24px",
          display: "flex",
          flexDirection: "column",
          gap: 14,
        }}
      >
        {machine.isDecommissioned === true && (
          <ProfileDecommissionBanner
            machine={machine}
            onRestore={onRestore}
            onDelete={onDelete}
            deleteBusy={deleteBusy}
          />
        )}

        <ProfileHero
          machine={machine}
          telemetry={telemetry}
          presentation={presentation}
          actions={actions}
          onCreateServiceReport={onCreateServiceReport}
          onCreateServiceRequest={onCreateServiceRequest}
        />

        <ProfileTabs tabs={tabs} active={activeTab} onChange={onTabChange} />

        {/* Top of the tab body area — placement note in MachineProfileV2.tsx. */}
        {readiness && (
          <ReadinessProfileCardV2
            presentation={readiness}
            oemPartnerName={machine.catalogModel?.oemPartnerName}
            manufacturerName={manufacturerName}
            onOpenDetails={() => onReadinessOpenChange(true)}
          />
        )}

        <div
          role="tabpanel"
          id="machinery-v2-tabpanel"
          aria-labelledby={`machinery-v2-tab-${activeTab}`}
          style={{ minWidth: 0 }}
        >
          <ProfileTabBody tab={activeTab} machine={machine} />
        </div>
      </main>

      {/* Mounted at PAGE level, outside every clickable surface — the fleet's
          rule, for the same reason. ReadinessPopupV2 also carries the standing
          stopPropagation guard on BOTH Dialog.Overlay and Dialog.Content. */}
      {readiness && readinessOpen && (
        <ReadinessPopupV2
          open
          onOpenChange={onReadinessOpenChange}
          presentation={readiness}
          machineOrModelName={getMachineDisplayName(machine)}
          oemPartnerName={machine.catalogModel?.oemPartnerName}
          manufacturerName={manufacturerName}
        />
      )}

      {/* The sanctioned v2 feedback primitive. */}
      <MachineryToastHost />

      <ProfileDialogs machine={machine} {...dialogProps} />
    </div>
  );
};

export default ProfileBody;
